import {computed, defineComponent, type PropType, reactive, ref, watch} from 'vue';
import {ActorType, OperationsActorDto, OperationsActorInfo} from "@/api/enterprise-gateway-api.proxy.ts";
import * as yup from "yup";
import {checkVAT, countries} from "jsvat";
import {useForm} from "vee-validate";
import {useOrganisationStore} from "@/app/stores/organisation-store.ts";
import {storeToRefs} from "pinia";
import {useModalEventsUtil} from "@/app/shared/helpers/modal-events-util.ts";
import {getIsoCodeByName, getNameByIsoCode} from "@/app/shared/helpers/country.ts";
import type {IBillingAddress} from "@/app/shared/components/aeg-address-form/aeg-address-form.ts";
import {Button, Dialog} from 'primevue';
import AegCancelButton from '@/app/admin/components/aeg-cancel-button/aeg-cancel-button.vue';

export const editActorModal = 'edit-actor-details-modal';

export const actorValidationSchema = yup.object({
    street: yup.string().required('Straat is verplicht'),
    houseNumber: yup.number().typeError('Voer een geldig huisnummer in').required('Huisnummer is verplicht'),
    boxNumber: yup.string().nullable(),
    postalCode: yup.string().required('Postcode is verplicht'),
    city: yup.string().required('Gemeente is verplicht'),
    country: yup.string().default('België').required('Land is verplicht'),
    vatNumber: yup.string().when('showVatNumber', {
        is: true,
        then: (schema) => schema
            .required('BTW-nummer is verplicht')
            .test('vat-number', 'Het opgegeven btw-nummer is ongeldig. Contacteer accounting@athumi.eu indien u toch facturen op dit btw-nummer wenst.', (value) => {
                const vatCheckResult = checkVAT(value, countries);
                return vatCheckResult.isValid;
            }),
        otherwise: (schema) => schema.nullable()
    }),
    showVatNumber: yup.boolean()
});


const FORM_INITIAL_VALUES: ActorFormData = {
    billingAddress: {
        street: '',
        houseNumber: '',
        boxNumber: '',
        postalCode: '',
        city: '',
        country: 'België',
        emails: [],
        vatNumber: '',
        showVatNumber: false,
        isCitizen: false,
        showEmail: false,
    }
};

export interface ActorFormData {
    billingAddress: IBillingAddress;
}

export default defineComponent({
    name: 'AegEditActor',
    components: {AegCancelButton, Dialog, Button },
    props: {
        visible: { type: Boolean, default: false },
        actor: {
            type: Object as PropType<OperationsActorDto>,
            required: true
        }
    },
    emits: ['on-save', 'update:visible'],
    setup: function (props, {emit}) {
        const actor = ref<OperationsActorDto>(props.actor);
        const organisationStore = useOrganisationStore();
        const {isSavingActorDetails} = storeToRefs(organisationStore);

        const visibleModel = computed({
            get: () => props.visible,
            set: (value) => emit('update:visible', value)
        });

        const {defineField, handleSubmit, errors} = useForm<ActorFormData>({
            validationSchema: actorValidationSchema,
            initialValues: FORM_INITIAL_VALUES,
            validateOnMount: false,
            keepValuesOnUnmount: false,
        });

        const [billingAddress] = defineField('billingAddress');

        const mapActorToBillingAddress = (): IBillingAddress => ({
            street: actor.value.street ?? FORM_INITIAL_VALUES.billingAddress.street,
            houseNumber: actor.value.streetNumber ?? FORM_INITIAL_VALUES.billingAddress.houseNumber,
            boxNumber: actor.value.bus ?? FORM_INITIAL_VALUES.billingAddress.boxNumber,
            postalCode: actor.value.postCode ?? FORM_INITIAL_VALUES.billingAddress.postalCode,
            city: actor.value.city ?? FORM_INITIAL_VALUES.billingAddress.city,
            country: getNameByIsoCode(actor.value.countryRegionCode!) ?? FORM_INITIAL_VALUES.billingAddress.country,
            showEmail: false,
            showVatNumber: actor.value.actorType !== ActorType.Organisation && actor.value.actorType !== ActorType.Citizen,
            vatNumber: actor.value.vatNumber ?? FORM_INITIAL_VALUES.billingAddress.vatNumber,
            isCitizen: actor.value.actorType === ActorType.Citizen,
            emails: []
        });

        const title = computed(() => {
            return `Actor aanpassen (${actor.value.name} - ${actor.value.identifier})`;
        });

        const formState = reactive({
            isAddressValid: true,
        });

        const addressValidated = (valid: boolean) => {
            formState.isAddressValid = valid
        };

        const actorChanged = async (newActor: OperationsActorDto) => {
            actor.value = newActor;
            billingAddress.value = mapActorToBillingAddress();
        };

        const handleFormSubmit = async () => {
            const operationsActorInfo = OperationsActorInfo.fromJS({
                street: billingAddress.value.street,
                streetNumber: billingAddress.value.houseNumber,
                bus: billingAddress.value.boxNumber,
                postCode: billingAddress.value.postalCode,
                city: billingAddress.value.city,
                countryCode: getIsoCodeByName(billingAddress.value.country),
            });

            if(billingAddress.value.showVatNumber && billingAddress.value.vatNumber) {
                operationsActorInfo.vatNumber = billingAddress.value.vatNumber;
            }

            await organisationStore.saveActor(actor.value.actorId!, operationsActorInfo);
            emit('on-save', {
                address: {...billingAddress.value}
            });
            onCloseModal();
        };

        const invalidForm = computed((): boolean =>  {
            return !formState.isAddressValid || isSavingActorDetails.value;
        });

        const onCloseModal = () => {
            visibleModel.value = false;
        }

        const onSubmit = handleSubmit(handleFormSubmit);

        watch(() => props.actor, actorChanged, {immediate: true, deep: true});

        return {
            visibleModel,
            billingAddress,
            editActorModal,
            isSavingActorDetails,
            invalidForm,
            title,
            actor,
            errors,
            onSubmit,
            handleFormSubmit,
            onCloseModal,
            addressValidated,
        }
    }
});
